import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Divider, List, ListItemText, Popover, Theme, Tooltip, useMediaQuery } from '@mui/material'
import AccountCircle from '@mui/icons-material/AccountCircle'
import makeStyles from '@mui/styles/makeStyles'
import { usePopover } from 'app/menu/components/TopBar/use-popover'
import { TopBarMenuItem } from './TopBarMenuItem'
import { TopBarListItem } from './TopBarListItem'
import { IRootReducerState } from 'state/rootReducer'
import { IEmployee } from '@cbgms/api'
import FullName from '@cbgms/components/src/utils/FullName'
import { useTranslation } from 'react-i18next'
import { logout } from 'app/auth/state/actions'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import N from 'utils/navigation'
import { useFeature } from '@cbgms/base/feature-flags'

const useStyles = makeStyles({
  user: {
    minWidth: 160,
    maxWidth: 360
  },
  textOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  inline: {
    display: 'inline'
  }
})

export const UserMenu: React.FC = () => {
  const classes = useStyles()
  const location = useLocation()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const employee = useSelector<IRootReducerState, IEmployee>(state => state.auth.employee)
  const [userEl, userOpen, handleUserClick, handleUserClose] = usePopover()
  const isPortal = useIsPortal()
  const match = useRouteMatch<{ workorderUUID: string }>('/orders/:workorderUUID')
  const workorderUUID = match?.params.workorderUUID
  const isTopBarUXChangeForTimerEnabled = useFeature('topbar-ux-change-for-timer')
  const isSmallerThanXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'))

  const handleLogout = () => {
    // check if logout from order page
    if (isPortal && workorderUUID) {
      const currentPath = location.pathname + location.search + location.hash
      const encodedPath = encodeURIComponent(currentPath)
      const loginUrl = `/auth/login?redirect=${encodedPath}`
      handleUserClose()
      N.push(loginUrl, { pendingLogout: true })
    } else {
      handleUserClose()
      dispatch(logout())
      setTimeout(() => window.location.reload(), 1000)
    }
  }

  return (
    <>
      {isTopBarUXChangeForTimerEnabled && isSmallerThanXL ? (
        <TopBarMenuItem isMenu badge={0} onClick={handleUserClick} data-testid='topbar-user'>
          <Tooltip title={<FullName firstName={employee.FirstName} prefix={employee.NamePrefix} lastName={employee.Name} />}>
            <AccountCircle />
          </Tooltip>
        </TopBarMenuItem>
      ) : (
        <TopBarMenuItem isMenu badge={0} onClick={handleUserClick} startIcon={<AccountCircle />} data-testid='topbar-user'>
          <FullName firstName={employee.FirstName} prefix={employee.NamePrefix} lastName={employee.Name} />
        </TopBarMenuItem>
      )}
      <Popover
        open={userOpen}
        anchorEl={userEl}
        onClose={handleUserClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <List className={classes.user}>
          <TopBarListItem data-testid='user-settings' link='/user-settings' onClick={handleUserClose}>
            <ListItemText primary={t('Auth.BtnSettings')} />
          </TopBarListItem>
          <Divider component='li' />
          <TopBarListItem
            data-testid='user-logout'
            onClick={() => {
              handleLogout()
            }}
          >
            <ListItemText primary={t('Auth.BtnLogout')} />
          </TopBarListItem>
        </List>
      </Popover>
    </>
  )
}
